import React, { useRef, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider, css } from 'styled-components';
import { formatPrice } from '@codex/helpers';
import { ProductPriceBlock } from '@codex-brightworld/basics';
import brightworldTheme from '@codex-brightworld/themes';
import { BrightWorldGlobalFonts } from '../BrightWorldGlobalFonts';

export const VARIANT_FO_SITE_LISTING_PAGE_LIST = 'listing_list';
export const VARIANT_FO_SITE_LISTING_PAGE_GRID = 'listing_grid';
export const VARIANT_FO_SITE_PRODUCT_PAGE = 'product';
export const VARIANT_FO_SITE_PSA = 'psa';
export const VARIANT_FO_SITE_COMPARE_POPUP = 'compare_popup';
export const VARIANT_FO_SITE_COMPARE_SLIDER = 'compare_slider';

const MAX_GRID_CELL_WIDTH_TO_DISPLAY_WAS_PRICE = 190;

const CountdownSaleWrapper = styled.p`
  font-family: Helvetica, Arial, sans-serif;
  font-size: 13px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
`;

const BundleInfoWrapper = styled.div`
  margin-top: 5px;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.shades[4]};
  font-family: Helvetica, Arial, sans-serif;
  font-size: 11px;
  text-align: center;
`;

const InStorePriceWrapper = styled.p`
  font-family: Helvetica, Arial, sans-serif;
  font-size: 13px;
  margin-top: 10px;
  text-align: center;
`;

const StyledProductPriceBlock = styled(ProductPriceBlock)`
  ${({ variant }) => css`
    ${variant === VARIANT_FO_SITE_COMPARE_SLIDER && css`
      display: inline-block;
    `};
  `}
`;

const getPriceAmount = (price) => (
  typeof price === 'string'
    ? parseFloat(price.replace(',', '').match(/\d+(\.\d+)?$/)[0], 10)
    : price
);

const ProductPriceBlockWrapper = ({
  className,
  currentPrice,
  currency,
  perMonthPrice,
  perMonthTranslation,
  savingsPrice,
  savingsPriceTranslation,
  wasPrice,
  wasPriceTranslation,
  wasPriceFrom,
  wasPriceTo,
  displayWasPriceRange,
  fromDateTranslation,
  toDateTranslation,
  isOutOfStock,
  mainProductWasPrice,
  mainProductCurrentPrice,
  mainProductWasPriceTranslation,
  mainProductCurrentPriceTranslation,
  separateSellingPrice,
  separateSellingPriceTranslation,
  displayBundlePriceInsidePriceBlock,
  inStorePrice,
  inStorePriceTranslation,
  displayInStorePriceInsidePriceBlock,
  isFlashSale,
  countdownSaleTranslation,
  webExclusivePriceTranslation,
  variant,
  buyTogetherPrice
}) => {
  const thisComponent = useRef(null);
  const useDisplayWasPrice = () => {
    const [displayWasPrice, setDisplayWasPrice] = useState(true);
    useLayoutEffect(() => {
      const handleResize = () => {
        const priceBlockWidth = thisComponent.current.offsetWidth;
        setDisplayWasPrice(priceBlockWidth >= MAX_GRID_CELL_WIDTH_TO_DISPLAY_WAS_PRICE);
      };
      if (variant === VARIANT_FO_SITE_LISTING_PAGE_GRID) {
        window.addEventListener('resize', handleResize);
        handleResize();
      }
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    return displayWasPrice;
  };
  const displayWasPrice = useDisplayWasPrice();

  const priceAmount = perMonthPrice ? getPriceAmount(perMonthPrice) : getPriceAmount(currentPrice);
  let priceLabel = '';
  if (priceAmount) {
    priceLabel = formatPrice(priceAmount, false, currency);
  }

  const saveAmount = getPriceAmount(savingsPrice);
  let savingsPriceLabel = '';
  if (saveAmount > 0) {
    const formattedSaveAmount = formatPrice(saveAmount, false, currency);
    savingsPriceLabel = `${savingsPriceTranslation} ${formattedSaveAmount}`;
  }

  const wasPriceAmount = getPriceAmount(wasPrice);
  let wasPriceLabel = '';
  if (wasPriceAmount) {
    const formattedWasPrice = formatPrice(wasPriceAmount, false, currency);
    wasPriceLabel = `${wasPriceTranslation} ${formattedWasPrice}`;
  }


  let mainProductPriceLabel = '';
  if (mainProductWasPrice && mainProductCurrentPrice) {
    const mainProductWasPriceAmount = getPriceAmount(mainProductWasPrice);
    const formattedMainProductWasPrice =
      formatPrice(mainProductWasPriceAmount, false, currency);
    const mainProductCurrentPriceAmount = getPriceAmount(mainProductCurrentPrice);
    const formattedMainProductCurrentPrice =
      formatPrice(mainProductCurrentPriceAmount, false, currency);

    mainProductPriceLabel =
    `${mainProductWasPriceTranslation} ` +
    `${formattedMainProductWasPrice}, ` +
    `${mainProductCurrentPriceTranslation} ` +
    `${formattedMainProductCurrentPrice}.`;
  }

  let separateSellingPriceLabel = '';
  if (separateSellingPrice) {
    const separateSellingPriceAmount = getPriceAmount(separateSellingPrice);
    const formattedSeparateSellingPrice =
      formatPrice(separateSellingPriceAmount, false, currency);
    separateSellingPriceLabel =
      `${separateSellingPriceTranslation} ${formattedSeparateSellingPrice}`;
  }

  let inStorePriceLabel = '';
  if (inStorePrice) {
    const inStorePriceAmount = getPriceAmount(inStorePrice);
    const formattedInStorePrice = formatPrice(inStorePriceAmount, false, currency);
    inStorePriceLabel = `${inStorePriceTranslation} ${formattedInStorePrice}`;
  }

  let formattedBuyTogetherPrice = null;
  if (buyTogetherPrice) {
    formattedBuyTogetherPrice = formatPrice(buyTogetherPrice, true, currency);
  }

  return (
    <ThemeProvider theme={brightworldTheme.brightworld}>
      <BrightWorldGlobalFonts />

      <>
        {isFlashSale && countdownSaleTranslation && (
          <CountdownSaleWrapper
            dangerouslySetInnerHTML={{ __html: countdownSaleTranslation }}
          />
        )}
        <div ref={thisComponent}>
          <StyledProductPriceBlock
            className={className}
            currentPrice={priceLabel}
            perMonthPrice={perMonthPrice}
            perMonthTranslation={perMonthTranslation}
            currency={currency}
            savingsPrice={savingsPriceLabel}
            wasPrice={wasPriceLabel}
            wasPriceFrom={wasPriceFrom}
            wasPriceTo={wasPriceTo}
            displayWasPrice={displayWasPrice}
            displayWasPriceRange={displayWasPriceRange}
            fromDateTranslation={fromDateTranslation}
            toDateTranslation={toDateTranslation}
            displayBundlePriceInsidePriceBlock={displayBundlePriceInsidePriceBlock}
            mainProductPriceLabel={mainProductPriceLabel}
            separateSellingPriceLabel={separateSellingPriceLabel}
            outOfStock={isOutOfStock}
            mainProductWasPrice={mainProductWasPrice}
            mainProductCurrentPrice={mainProductCurrentPrice}
            mainProductWasPriceTranslation={mainProductWasPriceTranslation}
            mainProductCurrentPriceTranslation={mainProductCurrentPriceTranslation}
            separateSellingPrice={separateSellingPrice}
            separateSellingPriceTranslation={separateSellingPriceTranslation}
            inStorePriceLabel={inStorePriceLabel}
            displayInStorePriceInsidePriceBlock={displayInStorePriceInsidePriceBlock}
            isFlashSale={isFlashSale}
            countdownSaleTranslation={countdownSaleTranslation}
            webExclusivePriceTranslation={webExclusivePriceTranslation}
            variant={variant}
            buyTogetherPrice={formattedBuyTogetherPrice}
          />
        </div>
        {(mainProductPriceLabel || separateSellingPriceLabel) &&
          !displayBundlePriceInsidePriceBlock && (
          <BundleInfoWrapper>
            {mainProductPriceLabel}
            {`${mainProductPriceLabel ? '\n' : ''}`}
            {separateSellingPriceLabel}
          </BundleInfoWrapper>
        )}
        {inStorePriceLabel && !displayInStorePriceInsidePriceBlock && (
          <InStorePriceWrapper>
            {inStorePriceLabel}
          </InStorePriceWrapper>
        )}
      </>
    </ThemeProvider>
  );
};

ProductPriceBlockWrapper.propTypes = {
  className: PropTypes.string,
  currentPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  currency: PropTypes.string,
  perMonthPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  perMonthTranslation: PropTypes.string,
  savingsPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  savingsPriceTranslation: PropTypes.string,
  wasPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  wasPriceTranslation: PropTypes.string,
  wasPriceFrom: PropTypes.string,
  wasPriceTo: PropTypes.string,
  displayWasPriceRange: PropTypes.bool,
  fromDateTranslation: PropTypes.string,
  toDateTranslation: PropTypes.string,
  isOutOfStock: PropTypes.bool,
  mainProductWasPrice: PropTypes.string,
  mainProductCurrentPrice: PropTypes.string,
  mainProductWasPriceTranslation: PropTypes.string,
  mainProductCurrentPriceTranslation: PropTypes.string,
  separateSellingPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  separateSellingPriceTranslation: PropTypes.string,
  displayBundlePriceInsidePriceBlock: PropTypes.bool,
  inStorePrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  inStorePriceTranslation: PropTypes.string,
  displayInStorePriceInsidePriceBlock: PropTypes.bool,
  isFlashSale: PropTypes.bool,
  countdownSaleTranslation: PropTypes.string,
  webExclusivePriceTranslation: PropTypes.string,
  variant: PropTypes.string,
  buyTogetherPrice: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ])
};

ProductPriceBlockWrapper.defaultProps = {
  className: undefined,
  currentPrice: undefined,
  perMonthTranslation: 'per month',
  currency: 'GBP',
  perMonthPrice: undefined,
  savingsPrice: undefined,
  savingsPriceTranslation: 'Save',
  wasPrice: undefined,
  wasPriceTranslation: 'Was',
  wasPriceFrom: undefined,
  wasPriceTo: undefined,
  displayWasPriceRange: true,
  fromDateTranslation: undefined,
  toDateTranslation: undefined,
  isOutOfStock: false,
  mainProductWasPrice: undefined,
  mainProductCurrentPrice: undefined,
  mainProductWasPriceTranslation: 'Main product was',
  mainProductCurrentPriceTranslation: 'now',
  separateSellingPrice: undefined,
  separateSellingPriceTranslation: 'Separate selling price',
  displayBundlePriceInsidePriceBlock: false,
  inStorePrice: undefined,
  inStorePriceTranslation: 'Price in store',
  displayInStorePriceInsidePriceBlock: false,
  isFlashSale: false,
  countdownSaleTranslation: undefined,
  webExclusivePriceTranslation: undefined,
  variant: undefined,
  buyTogetherPrice: undefined
};

export default ProductPriceBlockWrapper;
